// ═══════════════════════════════════════════════════════════════════════════
// ClientAccounts — el dinero
//
// Responde "¿quién me debe?": la misma información que Historial, pero
// ordenada por cliente y con los saldos al frente. Es una pregunta que cruza
// clientes, así que no la sustituye ninguna ficha individual.
//
// Las acciones sobre cada remisión son LAS MISMAS que en Historial y en la
// ficha del cliente — vienen de `useRemisionAcciones`. Antes aquí solo se
// bajaban PDFs, y el chip de "por facturar" era un adorno cuyo tooltip decía
// "timbra desde Historial": la pantalla que te dice qué falta facturar era la
// única donde no se podía facturar.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { fmt } from "../pdf/comunes.js";
import { Card, SectionTitle } from "./basicos.jsx";
import { inputStyle, selectStyle } from "./estilos.js";

import { resumenCartera, estadoFacturacion, estadoCobranza, saldoPendiente, saldoAFavor,
         totalPagado, requiereREP } from "../dominio/cobranza.js";
import { esperaFolio } from "../datos/remisiones-pg.js";
import { useRemisionAcciones } from "./RemisionAcciones.jsx";
import { QuienMeDebe } from "./QuienMeDebe.jsx";

export function ClientAccounts({ clients, invoices, products, setInvoices, onIrAConfig,
                                 membresia, onAbrirEnEditor }) {
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCfdi, setFilterCfdi] = useState("all"); // all | timbrada | pendiente

  // Group invoices by client
  const clientInvoices = useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const cId = inv.client?.id;
      if (!cId) return;
      if (!map[cId]) map[cId] = [];
      map[cId].push(inv);
    });
    // Sort each client's invoices by date desc
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(b.date) - new Date(a.date)));
    return map;
  }, [invoices]);

  // Build client summary
  const summaries = useMemo(() => {
    return clients.map(c => {
      const invs = clientInvoices[c.id] || [];
      const total = invs.reduce((s, i) => s + (i.total || 0), 0);
      const timbradas = invs.filter(i => i.cfdiId);
      const pendientes = invs.filter(i => !i.cfdiId);
      const canceladas = invs.filter(i => i.cfdiStatus === "canceled");
      const lastInv = invs[0];
      // Lo PENDIENTE, visible sin abrir la tarjeta (observación del operador).
      const vivas = invs.filter(i => i.cfdiStatus !== "canceled" && !i.canceledAt);
      const porCobrar = vivas.filter(i => saldoPendiente(i) > 0);
      const porFacturar = vivas.filter(i => estadoFacturacion(i).clave === "por_facturar");
      const saldoTotal = porCobrar.reduce((t, i) => t + saldoPendiente(i), 0);
      // Lo que ENTRÓ DE MÁS. Va aparte del saldo por cobrar: no se resta.
      // Restarlo daría un neto que esconde las dos cosas — una se cobra y la
      // otra se devuelve o se revierte, y no se persiguen igual.
      //
      // (Aplicarlo a otra remisión NO existe todavía. Se dijo en un texto de
      //  la interfaz y el operador lo cazó: no hay que prometerlo hasta que
      //  esté.)
      // El de la CUENTA del cliente. Antes se sumaban los excedentes documento
      // por documento, que es otra cosa: un excedente en la 640 no servía para
      // la 651. El operador lo corrigió — "el saldo a favor no debe estar
      // asociado a una remisión" — y ahora lo calcula la base.
      const aFavorTotal = Number(c.aFavor) || vivas.reduce((t, i) => t + saldoAFavor(i), 0);
      return { client: c, invoices: invs, total, timbradas: timbradas.length, pendientes: pendientes.length,
               canceladas: canceladas.length, lastDate: lastInv?.date, count: invs.length,
               porCobrar, porFacturar, saldoTotal, aFavorTotal };
    }).filter(s => s.count > 0)
      .sort((a, b) => (b.lastDate || "").localeCompare(a.lastDate || ""));
  }, [clients, clientInvoices]);

  // Al pedir "con saldo por cobrar" se ordena por lo que deben, no por la
  // última venta: es la pregunta que hace quien persigue cobranza.
  const ordenadas = useMemo(
    () => filterCfdi === "sin_pagar"
      ? [...summaries].sort((a, b) => b.saldoTotal - a.saldoTotal)
      : summaries,
    [summaries, filterCfdi]);

  const filtered = ordenadas.filter(s => {
    const matchSearch = !search || s.client.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filterCfdi === "all" ? true :
      filterCfdi === "timbrada" ? s.timbradas > 0 :
      filterCfdi === "pendiente" ? s.pendientes > 0 :
      filterCfdi === "por_facturar" ? s.invoices.some(i => estadoFacturacion(i).clave === "por_facturar") :
      filterCfdi === "sin_pagar" ? s.invoices.some(i => i.cfdiStatus !== "canceled" && saldoPendiente(i) > 0) :
      true;
    return matchSearch && matchFilter;
  });

  // Resumen por las DOS dimensiones (facturación y cobranza) — lote 2.5
  const cartera = useMemo(
    () => resumenCartera(filtered.flatMap(s => s.invoices)),
    [filtered]);

  // El mismo juego de acciones que en Historial: pagos, timbrado, PDFs,
  // cancelaciones. Los diálogos se montan una sola vez, al final.
  const { barraDe, modales, ejecutar, accionDisponible, abrirPagos } = useRemisionAcciones({
    clients, products, invoices, setInvoices, membresia, onAbrirEnEditor, onIrAConfig });

  const grandTotal = filtered.reduce((s, c) => s + c.total, 0);
  const grandCount = filtered.reduce((s, c) => s + c.count, 0);
  const grandTimbradas = filtered.reduce((s, c) => s + c.timbradas, 0);

  return (
    <div>
      <SectionTitle sub={`${filtered.length} clientes · ${grandCount} remisiones · ${fmt(grandTotal)} total`}>
        Cuentas por Cliente
      </SectionTitle>

      {/* Primero la pregunta que más se hace: ¿quién me debe, y cuánto? */}
      <QuienMeDebe clients={clients} invoices={invoices} abrirPagos={abrirPagos}
        onVerCliente={(id) => {
          setSearch(""); setFilterCfdi("all"); setExpandedId(id);
          setTimeout(() => document.getElementById(`cuenta-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
        }} />

      {/* Cartera: las DOS dimensiones. Antes todo se sumaba en un solo número. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 12, marginBottom: 16 }}>
        <Card>
          <div style={{ fontSize: 10, color: "#70c9c9", letterSpacing: 2, marginBottom: 10 }}>🧾 FACTURACIÓN</div>
          {[["facturada","Facturadas"],["por_facturar","Por facturar"],["publico_general","Público en general"],["cancelada","Canceladas"]]
            .map(([k, etq]) => {
              const d = cartera.facturacion[k];
              if (!d.n) return null;
              const color = { facturada:"#4ade80", por_facturar:"#facc15", publico_general:"#70a0c9", cancelada:"#c97070" }[k];
              return (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12 }}>
                  <span style={{ color }}>{etq} <span style={{ color: "#555" }}>({d.n})</span></span>
                  <span style={{ color: "#e8e8e8" }}>{fmt(d.importe)}</span>
                </div>
              );
            })}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #1a1a1a", fontSize: 10, color: "#666" }}>
            Sin RFC → se ampara en factura global
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 10, color: "#70c9c9", letterSpacing: 2, marginBottom: 10 }}>💰 COBRANZA</div>
          {[["pagada","Pagadas"],["parcial","Parciales"],["pendiente","Pendientes"]].map(([k, etq]) => {
            const d = cartera.cobranza[k];
            if (!d.n) return null;
            const color = { pagada:"#4ade80", parcial:"#facc15", pendiente:"#c97070" }[k];
            return (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12 }}>
                <span style={{ color }}>{etq} <span style={{ color: "#555" }}>({d.n})</span></span>
                <span style={{ color: "#e8e8e8" }}>{fmt(d.importe)}</span>
              </div>
            );
          })}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #1a1a1a", display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "#c9a96e" }}>Por cobrar</span>
            <span style={{ color: "#c9a96e", fontWeight: 700 }}>{fmt(cartera.porCobrar)}</span>
          </div>
          {cartera.repPendientes > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#c9a96e" }}>
              ⚠️ {cartera.repPendientes} factura(s) PPD con pagos: requieren complemento de pago (REP)
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente..."
          style={{ ...inputStyle, flex: 1, minWidth: 150 }} />
        <select value={filterCfdi} onChange={e => setFilterCfdi(e.target.value)} style={{ ...selectStyle, width: "auto" }}>
          <option value="all">Todas</option>
          <option value="timbrada">Con CFDI</option>
          <option value="pendiente">Sin CFDI</option>
          <option value="por_facturar">Por facturar (con RFC)</option>
          <option value="sin_pagar">Con saldo por cobrar</option>
        </select>
      </div>

      {modales}

      {filtered.length === 0 ? (
        <Card><p style={{ color: "#666", textAlign: "center" }}>No hay remisiones registradas{search ? " para esta búsqueda" : ""}.</p></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(s => {
            const isExpanded = expandedId === s.client.id;
            return (
              <div key={s.client.id} id={`cuenta-${s.client.id}`}>
              <Card style={{ cursor: "pointer", border: isExpanded ? "1px solid #3a3a1a" : undefined }}
                onClick={() => setExpandedId(isExpanded ? null : s.client.id)}>
                {/* Summary row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#e8e0d4" }}>{s.client.name}</div>
                    <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>
                      {s.client.city}{s.client.state ? ", " + s.client.state : ""}
                      {s.lastDate && (" · Última: " + new Date(s.lastDate).toLocaleDateString("es-MX"))}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#c9a96e" }}>{fmt(s.total)}</div>
                    <div style={{ display: "flex", gap: 6, marginTop: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#111", color: "#888" }}>
                        {s.count} remisión{s.count !== 1 ? "es" : ""}
                      </span>
                      {s.timbradas > 0 && (
                        <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#0d1f0d", color: "#4ade80" }}>
                          {s.timbradas} timbrada{s.timbradas !== 1 ? "s" : ""}
                        </span>
                      )}
                      {s.pendientes > 0 && (
                        <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#1f1a0d", color: "#facc15" }}>
                          {s.pendientes} sin CFDI
                        </span>
                      )}
                      {s.canceladas > 0 && (
                        <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#1f0d0d", color: "#f87171" }}>
                          {s.canceladas} cancelada{s.canceladas !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pendientes a la vista: antes había que expandir para verlos */}
                {(s.porCobrar.length > 0 || s.porFacturar.length > 0) && (
                  <div onClick={e => e.stopPropagation()}
                    style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #161616",
                             display: "flex", flexDirection: "column", gap: 6 }}>

                    {s.porCobrar.length > 0 && (
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, color: "#c97070", minWidth: 78 }}>
                          POR COBRAR
                        </span>
                        {s.porCobrar.slice(0, 6).map(inv => (
                          <button key={inv.id} onClick={() => abrirPagos(inv)}
                            title={`Registrar pago · ${new Date(inv.date).toLocaleDateString("es-MX")}`}
                            style={{ background: "#160f0f", border: "1px solid #3a1a1a", borderRadius: 4,
                                     color: "#e8b0b0", cursor: "pointer", padding: "2px 8px", fontSize: 11 }}>
                            {esperaFolio(inv) ? "s/folio" : `#${inv.folio}`} · {fmt(saldoPendiente(inv))}
                            {requiereREP(inv) ? " ⚠" : ""}
                          </button>
                        ))}
                        {s.porCobrar.length > 6 && (
                          <span style={{ fontSize: 10, color: "#666" }}>+{s.porCobrar.length - 6} más</span>
                        )}
                        <span style={{ fontSize: 11, color: "#c9a96e", marginLeft: "auto" }}>
                          {fmt(s.saldoTotal)}
                        </span>
                      </div>
                    )}

                    {/* Saldo a favor: el cliente pagó de más. No se resta del
                        por cobrar —son cosas distintas— y se destaca porque hay
                        que resolverlo: devolverlo, aplicarlo, o descubrir que
                        fue un pago duplicado. */}
                    {s.aFavorTotal > 0 && (
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8,
                                    flexWrap: "wrap", padding: "4px 0" }}>
                        <span style={{ fontSize: 10, color: "#70c9c9", minWidth: 78,
                                       letterSpacing: 1 }}>A FAVOR</span>
                        <span style={{ fontSize: 11, color: "#7a9a9a", flex: 1 }}>
                          dinero suyo sin aplicar — se puede usar en cualquier remisión
                        </span>
                        <span style={{ fontSize: 12, color: "#70c9c9", fontWeight: 600,
                                       marginLeft: "auto" }}>
                          {fmt(s.aFavorTotal)}
                        </span>
                      </div>
                    )}

                    {s.porFacturar.length > 0 && (
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, color: "#facc15", minWidth: 78 }}>
                          POR FACTURAR
                        </span>
                        {s.porFacturar.slice(0, 6).map(inv => (
                          // El chip pregunta lo MISMO que la barra. Si no se
                          // puede timbrar —sin Facturapi, sin CSF, sin permiso—
                          // no se ofrece un atajo que va a fallar.
                          (() => {
                          const listo = accionDisponible(inv, "timbrar");
                          return (
                          <button key={inv.id} disabled={!listo}
                            onClick={() => listo && ejecutar("timbrar", inv)}
                            title={listo
                              ? `${new Date(inv.date).toLocaleDateString("es-MX")} · clic para timbrar`
                              : `${new Date(inv.date).toLocaleDateString("es-MX")} · falta configurar el timbrado o los datos fiscales del cliente`}
                            style={{ background: "#161206", border: "1px solid #3a3a1a", borderRadius: 4,
                                     color: "#e8d9a0", cursor: listo ? "pointer" : "default",
                                     opacity: listo ? 1 : 0.55, padding: "2px 8px", fontSize: 11 }}>
                            {esperaFolio(inv) ? "s/folio" : `#${inv.folio}`} · {fmt(inv.total)}
                          </button>
                          ); })()
                        ))}
                        {s.porFacturar.length > 6 && (
                          <span style={{ fontSize: 10, color: "#666" }}>+{s.porFacturar.length - 6} más</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Expanded: invoice list */}
                {isExpanded && (
                  <div style={{ marginTop: 16, borderTop: "1px solid #1a1a1a", paddingTop: 12 }} onClick={e => e.stopPropagation()}>
                    {/* Renglones, no tabla. La tabla de siete columnas no cabía
                        en un teléfono: la celda de acciones quedaba de dos
                        dedos de ancho y los iconos se apilaban en una torre
                        fuera de lugar —lo reportó el operador—. Aquí los datos
                        van en un renglón que envuelve y las acciones en el
                        suyo, a lo ancho, en cualquier pantalla. */}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {s.invoices.map((inv, idx) => {
                        const botellas = inv.totalBottles || inv.lines?.reduce((s, l) => s + l.qty, 0) || "-";
                        return (
                          <div key={inv.id || `inv-${idx}`}
                               style={{ padding: "8px 0", borderBottom: "1px solid #1a1a1a" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12 }}>
                              <span style={{ fontWeight: 600, color: "#e8e0d4", minWidth: 52 }}>
                                {esperaFolio(inv) ? "s/folio" : `#${inv.folio}`}
                              </span>
                              <span style={{ color: "#888" }}>{new Date(inv.date).toLocaleDateString("es-MX")}</span>
                              <span style={{ color: "#666", fontSize: 11 }}>{botellas} bot.</span>
                              <span style={{ color: "#c9a96e", fontWeight: 600, marginLeft: "auto" }}>{fmt(inv.total)}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                          marginTop: 6 }}>
                              {inv.cfdiId ? (
                                inv.cfdiStatus === "canceled"
                                  ? <span style={{ color: "#f87171", fontSize: 10 }}>CFDI cancelado</span>
                                  : <span style={{ color: "#4ade80", fontSize: 10 }} title={inv.cfdiUuid || inv.cfdiId}>Timbrada</span>
                              ) : (
                                <span style={{ color: "#555", fontSize: 10 }}>Sin CFDI</span>
                              )}
                              {inv.cfdiStatus === "canceled" ? null : (() => {
                                const c = estadoCobranza(inv);
                                const saldo = saldoPendiente(inv);
                                return (
                                  <button onClick={() => abrirPagos(inv)}
                                    title={saldo > 0 ? `Saldo ${fmt(saldo)} — clic para registrar pago`
                                                     : "Clic para ver los movimientos o corregir"}
                                    style={{ background: "none", border: "1px solid #222", borderRadius: 4,
                                             color: c.color, cursor: "pointer", padding: "2px 8px", fontSize: 10 }}>
                                    {c.etiqueta}{saldo > 0 && totalPagado(inv) > 0 ? ` · falta ${fmt(saldo)}` : ""}
                                    {requiereREP(inv) ? " ⚠" : ""}
                                  </button>
                                );
                              })()}
                              {/* Las mismas que en Historial: timbrar, editar,
                                  cancelar y descargar, no solo los PDFs. */}
                              <div style={{ marginLeft: "auto" }}>{barraDe(inv, { compacto: true })}</div>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                                    padding: "8px 0", borderTop: "2px solid #333" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#888" }}>
                          {s.count} remisión{s.count !== 1 ? "es" : ""}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#c9a96e" }}>{fmt(s.total)}</span>
                      </div>
                    </div>

                    {/* Product breakdown */}
                    {(() => {
                      const productTotals = {};
                      s.invoices.forEach(inv => {
                        (inv.lines || []).forEach(li => {
                          if (!productTotals[li.productId]) productTotals[li.productId] = { name: li.productName, qty: 0, amount: 0 };
                          productTotals[li.productId].qty += li.qty;
                          productTotals[li.productId].amount += (li.amount || li.unitPrice * li.qty);
                        });
                      });
                      const sorted = Object.values(productTotals).sort((a, b) => b.amount - a.amount);
                      if (sorted.length === 0) return null;
                      return (
                        <div style={{ marginTop: 12, padding: 12, background: "#0a0a0a", borderRadius: 6 }}>
                          <div style={{ fontSize: 10, color: "#666", letterSpacing: 1, marginBottom: 8 }}>RESUMEN DE PRODUCTOS</div>
                          {sorted.map((p, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 11 }}>
                              <span style={{ color: "#888" }}>{p.name}</span>
                              <span style={{ color: "#c9a96e" }}>{p.qty} uds · {fmt(p.amount)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
